/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import { GtmDataLayer, GtmDimensions, GtmEventNames, GtmFlightMessagingData, GTMLists, GTMObjectEvents } from 'google-tag-manager';
import { getNumberOfSeatsLeft } from 'server-lib/domain/utils/flightDetailUtils';
import { GTMSharedContextStore } from './context';
import {
  getComponentSenderFromCookieIfAvailable,
  getFlightsRoute,
  getReturnDateFromSearchCookie,
  itemPriceDifference,
  pushToDataLayer
} from './utils/helpers';
import { SelectedFlightData } from '../apis/bff/methods/search/types';
import { DateTime } from 'luxon';
import { FunnelQueryCookieObject, parseFunnelQuery } from '../cookie-utils';
import { searchDataStore, SessionStorageService } from '../stores';
import { calculateTripDuration } from './utils/calculate-trip-duration';

type Products = GtmDataLayer;

export type FlightGridEcommerceDataLayer = {
  [GtmDimensions.event]: GtmEventNames.flightGridEcommerce;
  ecommerce: {
    click: {
      products: Products[];
    };
  };
};

export const pushFlightGridEcommerceAnalyticsData = (
  { flightSegmentId }: SelectedFlightData,
  isOutbound: boolean,
  flightQuery: FunnelQueryCookieObject
) => {
  const sharedContext = GTMSharedContextStore.getState();
  const flight = sharedContext.getFlightBySegmentId(flightSegmentId);
  const searchStore = searchDataStore.getState();
  const defaultCurrency = searchStore.metaData.currencyCode;
  const journeyType = parseFunnelQuery()?.returnDate ? 'Return' : 'One Way';
  const paxConfig = `A: ${flightQuery.numberOfAdults}, C: ${flightQuery.numberOfChildren}, I: ${flightQuery.numberOfInfants}`;
  const fareType = sharedContext.basket?.journeyPairs?.outboundJourney?.airfare.airfares[0].fareType;
  const numberOfSeatsLeft = getNumberOfSeatsLeft(flight);
  const showSeatsLeftAtMessage =
    sharedContext.flightGridProps && sharedContext.flightGridProps.maxSeatsAvailable >= numberOfSeatsLeft;

  const isSitecoreWasNowInBasketEnabled = sharedContext.basketProps?.enableWasNowPrices;
  const isSitecoreWasNowInFlightGridEnabled = sharedContext.flightGridProps?.enableWasNowPrices;

  const urgencyMessagePart1 = showSeatsLeftAtMessage
    ? `${numberOfSeatsLeft} ${numberOfSeatsLeft > 1 ? 'seats' : 'seat'} left at`
    : 'NULL';
  const urgencyMessagePart2 = flight?.fares?.ADT?.PROMOTIONAL !== undefined ? 'SALE' : 'NULL';
  const urgencyMessagePart3 =
    flight?.fares?.ADT?.PROMOTIONAL !== undefined && isSitecoreWasNowInBasketEnabled ? 'Flight fare sale' : 'NULL';
  const urgencyMessageContent = `${urgencyMessagePart1}|${urgencyMessagePart2}|${urgencyMessagePart3}`;

  const wasPriceValue =
    isSitecoreWasNowInFlightGridEnabled && flight?.fares?.ADT?.PROMOTIONAL?.unitPrice.grossPrice
      ? flight?.fares?.ADT?.STANDARD?.unitPrice.grossPrice
      : null;

  const dataLayer = {
    [GtmDimensions.event]: GtmEventNames.flightGridEcommerce,
    ecommerce: {
      click: {
        products: [
          {
            [GtmDimensions.category]: isOutbound ? 'Flight: Outbound' : 'Flight: Inbound',
            [GtmDimensions.currencyCode]: sharedContext.basket?.isoCurrencyCode || defaultCurrency || '', // TODO CDT-560 change logic for the fallback currency
            [GtmDimensions.timestamp]: DateTime.now().toFormat('y-MM-dd_HH:mm:ss'),
            [GtmDimensions.priceVariance]: flight ? sharedContext.getPriceVariance(flight, isOutbound) : '',
            [GtmDimensions.departureAirportCode]: flight?.departureAirportCode,
            [GtmDimensions.departureAirportCountryCode]: sharedContext.getAirportCountryCode(flightQuery.originIata) || '',
            [GtmDimensions.destinationAirportCode]: flight?.arrivalAirportCode,
            [GtmDimensions.destinationAirportCountryCode]: sharedContext.getAirportCountryCode(flightQuery.destinationIata) || '',
            [GtmDimensions.searchedMultipleDepartureAirports]: sharedContext.isSearchedMultipleAirports(flightQuery.originIata),
            [GtmDimensions.searchedMultipleDestinationAirports]: sharedContext.isSearchedMultipleAirports(
              flightQuery.destinationIata
            ),
            [GtmDimensions.journeyType]: journeyType,
            [GtmDimensions.departureDate]: DateTime.fromISO(flight?.localDepartureDateTime!).toFormat('y-MM-dd'),
            [GtmDimensions.flightTime]: DateTime.fromISO(flight?.localDepartureDateTime!).toFormat('HH:mm'),
            [GtmDimensions.daysToDeparture]: DateTime.fromISO(flight?.localDepartureDateTime!)
              .startOf('day')
              .diff(DateTime.now().startOf('day'), 'days').days,
            [GtmDimensions.tripDuration]: calculateTripDuration(journeyType, flight, flightQuery),
            [GtmDimensions.paxCount]: flightQuery.numberOfAdults + flightQuery.numberOfChildren,
            [GtmDimensions.paxConfig]: paxConfig,
            [GtmDimensions.noOfAdults]: flightQuery.numberOfAdults,
            [GtmDimensions.noOfChildren]: flightQuery.numberOfChildren,
            [GtmDimensions.noOfInfants]: flightQuery.numberOfInfants,
            [GtmDimensions.flightNumber]: `${flight?.icaoCarrierCode}${flight?.flightNumber}`,
            [GtmDimensions.flightRoute]: getFlightsRoute(flightQuery),
            [GtmDimensions.noOfFlightResults]: sharedContext.noOfFlights(),
            [GtmDimensions.minFlightPrice]: sharedContext.getMinPrices(),
            [GtmDimensions.maxFlightPrice]: sharedContext.getMaxPrices(),
            [GtmDimensions.resultsView]: '3 day view',
            [GtmDimensions.alternativeAirports]: '', // Need to discuss
            [GtmDimensions.noAlternativeAirports]: '', // Need to discuss
            [GtmDimensions.alternativeAirportSelected]: '', // Need to discuss
            [GtmDimensions.lowestFareSelected]: flight?.lowestFare ? 'Yes' : 'No',
            [GtmDimensions.showFlexiFares]: 'No', // This is not used in the flight grid, so we set it to 'No' by default
            [GtmDimensions.noOfFlexiFaresAvailable]: 0, // This is not used in the flight grid, so we set it to 0 by default
            [GtmDimensions.fareType]: fareType
              ? String(fareType).charAt(0).toUpperCase() + String(fareType).slice(1).toLowerCase()
              : 'Standard',
            [GtmDimensions.onsiteSearchOrigin]: getComponentSenderFromCookieIfAvailable(flightQuery),
            [GtmDimensions.eJPlusPassengers]: sharedContext.getEJPlusPassengers(),
            [GtmDimensions.urgencyMessageContent]: urgencyMessageContent,
            [GtmDimensions.urgencyMessageType]: showSeatsLeftAtMessage ? 'Flight' : '',
            [GtmDimensions.objectEvent]: GTMObjectEvents.pageload,
            [GtmDimensions.list]: GTMLists.flightGrid,
            [GtmDimensions.returnDate]: getReturnDateFromSearchCookie(),
            [GtmDimensions.noOfSoldOutFlights]: sharedContext.getNoOfSoldOutFlights(),
            [GtmDimensions.id]: `${flight?.icaoCarrierCode}${flight?.flightNumber}`,
            [GtmDimensions.name]: flight ? `${flight.departureAirportCode}-${flight.arrivalAirportCode}` : '',
            [GtmDimensions.position]: flight ? sharedContext.getFlightPosition(flight, isOutbound) : '',
            [GtmDimensions.price]:
              flight?.fares?.ADT?.PROMOTIONAL?.unitPrice.grossPrice || flight?.fares?.ADT?.STANDARD?.unitPrice.grossPrice || '',
            [GtmDimensions.was_price]: wasPriceValue,
            [GtmDimensions.item_price_difference]: isSitecoreWasNowInFlightGridEnabled
              ? itemPriceDifference(
                  flight?.fares?.ADT?.PROMOTIONAL?.unitPrice.grossPrice,
                  flight?.fares?.ADT?.STANDARD?.unitPrice.grossPrice
                )
              : null,
            [GtmDimensions.quantity]: flightQuery.numberOfAdults + flightQuery.numberOfChildren,
            [GtmDimensions.variant]: ''
          }
        ]
      }
    }
  };

  const GtmFlightMessagingDataOutboundOrInbound = {
    [GtmFlightMessagingData.urgencyMessage]: urgencyMessagePart1,
    [GtmFlightMessagingData.promoMessage]: urgencyMessagePart2,
    [GtmFlightMessagingData.basketDiscountMessage]: urgencyMessagePart3,
    [GtmFlightMessagingData.wasPrice]: wasPriceValue
  };

  isOutbound
    ? SessionStorageService.setItem(`GtmFlightMessagingDataOutbound`, GtmFlightMessagingDataOutboundOrInbound)
    : SessionStorageService.setItem(`GtmFlightMessagingDataInbound`, GtmFlightMessagingDataOutboundOrInbound);

  pushToDataLayer(dataLayer);
};
